import { useLocation, useNavigate } from "src/navigation"
import { Link } from "src/navigation";
import { useRoles } from "src/user/Auth"
import { useTranslation } from "react-i18next"
import { useDennemeyer, useIpRight, useSynchronize } from "src/renewal/dennemeyer/DennemeyerProvider"
import { useDocumentSettings } from "src/documents/DocumentsSettings"
import { useAgorumObject, useFindUuid } from "src/documents/backend"
import { useState } from "react"
import Modal from "../Modal"
import AutomaticBackgroundChanges from "./AutomaticBackgroundChanges"
import _ from "lodash"
import { useMember } from "src/patents/PatentsProvider"
import { family_member } from "src/data"
import { AfterUpdateAction, afterUpdateActionsStr, AutomaticUpdateAction, ManualUpdateAction } from "."
import { Member } from "src/patents/patents"
import { DmParams, toQueryParams } from "src/renewal/dennemeyer/utils"
import { isImported, renewalStatusExtraction } from "src/renewal/dennemeyer/state/modern_states"

export function MemberChanged() {
  const {hasAnnuities, hasDocuments} = useRoles()

  const location = useLocation()
  const {member} = useMember() 

  if (!hasAnnuities && !hasDocuments)
    return null

  const state = location.state ?? {}

  const changes = state[afterUpdateActionsStr] ?? []
  if (changes.length === 0 || member.familyMemberId === undefined)
    return null

  const {from, to} = state

  return <MemberChangedView {...{changes, from, to, hasAnnuities, hasDocuments}} />
}

function splitAutomaticChanges(changes: AfterUpdateAction[])  {
    return _.partition(changes, c => ['move-folder', 'update-reference'].includes(c)) as [AutomaticUpdateAction[], ManualUpdateAction[]]
}

function MemberChangedView(
    {changes, from, to, hasAnnuities, hasDocuments}: 
    {changes: AfterUpdateAction[], from?: Member, to: Member, hasAnnuities: boolean, hasDocuments: boolean}
) {
    const [isAutomaticDone, setIsAutomaticDone] = useState(false)
    const { member, family } = useMember()

    const { ipRightByMemberId, postIpRight, upIpRightByMemberId } = useDennemeyer()
    const pcIpRight = member.unitaryPatent ? upIpRightByMemberId[member.familyMemberId] : ipRightByMemberId?.[member.familyMemberId]
    const { ipRight: ipRightResp, isLoading: isLoadingIpRight } = useIpRight(pcIpRight?.dennemeyerId)
    const ipRight = ipRightResp?.Data

    const renewalStatus = renewalStatusExtraction({ member: to, pcIpRight, ipRight })
    const imported = isImported(renewalStatus)

    const { triggerSynchronize } = useSynchronize()

    function updateReference() {
        // console.log("Updating ref with DM")
        return triggerSynchronize([pcIpRight.ipRightId])
    }

    async function stopCaseWithDm() {
        if (pcIpRight) {
            await postIpRight({ ...pcIpRight, status: 'Inactive' })
            await triggerSynchronize([pcIpRight?.ipRightId])
        }
    }

    const { number } = useDocumentSettings()
    const { uuid } = useFindUuid(number, family_member, from?.internalReference, family.internalReference)
    const { object, update, isLoading } = useAgorumObject({ uuid })

    async function moveFolder() {
        if (uuid === object?.uuid) {
            // console.log("Moving folder")
            return update({ ...object, name: to.internalReference })
        }
    }

    const hasFolder = uuid !== undefined
    const dmRefNotUpdated = ipRight?.CustomerReferenceNumbers?.[0] !== to.internalReference

    const actions = changes.filter(c =>
        (c === 'move-folder' && hasDocuments && hasFolder) ||
        (c === 'initiate-payment-handling' && hasAnnuities) ||
        (c === 'stop-payment-handling' && hasAnnuities && imported) ||
        (c === 'update-reference' && hasAnnuities && imported && dmRefNotUpdated)
    )

    // manual can only be empty, 'initiate-payment-handling' or 'stop-payment-handling'
    const [automatic, manual] = splitAutomaticChanges(actions)

    const automaticActions = automatic.map(a => {
        switch (a) {
            case 'move-folder':
                return { key: a, label: 'Updating data. Please wait a moment...', action: moveFolder }
            case 'update-reference':
                return { key: a, label: 'Updating data. Please wait a moment...', action: updateReference }
        }
    }).filter(Boolean)

    const manualAction = manual[0]
    const params: DmParams = {
        familyMemberId: member.familyMemberId,
        // PC IP Right can be also the Unitary patent
        ipRightId: pcIpRight?.ipRightId,
        dennemeyerId: pcIpRight?.dennemeyerId,
    }

    return <>
        {manualAction && <ChangePaymentHandling {...{manualAction, params, stopCaseWithDm}} />}
        {!isAutomaticDone && !isLoading && !isLoadingIpRight &&
            <AutomaticBackgroundChanges {...{
                actions: automaticActions,
                close: () => setIsAutomaticDone(true)
            }} />}
    </>
}

function ChangePaymentHandling(
    {manualAction, params, stopCaseWithDm}:
    {manualAction: 'initiate-payment-handling' | 'stop-payment-handling', params: DmParams, stopCaseWithDm: () => Promise<void>}) {
    const {t} = useTranslation()
    const navigate = useNavigate()

    const doInitiate = manualAction === 'initiate-payment-handling'

    const initiateLink = `renewals?${toQueryParams(params)}`

    async function stop() {
        await stopCaseWithDm()
        navigate('.')
    }

    return (
        <Modal escAction={() => navigate('.')}>
            <div className="p-4 space-y-2">
                <h2>You have changed the status of your patent</h2>
                <p className="text-slate-500">{t(doInitiate ? 'initiate-payment-handling-long' : 'stop-payment-handling-long')}</p>
            </div>
            <div className="bg-pcx-200 flex flex-row-reverse gap-4 p-4">
                {doInitiate
                    ? <Link
                        className="btn-primary"
                        to={initiateLink}
                    >{t('initiate-payment-handling')}</Link>
                    : <button className="btn-primary" onClick={stop}>{t('stop-payment-handling')}</button>}
                <Link to="." className="btn-secondary">{t('decide-later')}</Link>
            </div>
        </Modal>
    )
}